'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import React, { useEffect } from 'react';

import { getTrackingIcon } from '@skrym/shared/components/milestone-icon';
import { ScrollArea, ScrollBar } from '@skrym/shared/ui/scroll-area';
import { cn } from '@skrym/shared/utils/cn';

import type { models } from '../lib/api-client/encore-client';

export interface TrackingTimelineProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof timelineVariants> {
  milestones: models.MilestoneSnapshot[];
  locale: string;
}

const timelineVariants = cva('grid px-6 pb-6', {
  variants: {
    orientation: {
      vertical:
        'grid-flow-row grid-cols-[max-content_max-content_minmax(0,1fr)] grid-rows-none gap-x-4 gap-y-0',
      horizontal:
        'grid-flow-col grid-cols-none grid-rows-[max-content_max-content_minmax(0,1fr)] justify-items-center justify-stretch gap-x-0 gap-y-2 w-full',
      auto: 'grid-flow-row @3xl:grid-flow-col grid-cols-[max-content_max-content_minmax(0,1fr)] @3xl:grid-cols-none grid-rows-none @3xl:grid-rows-[max-content_max-content_minmax(0,1fr)] @3xl:justify-items-center @3xl:justify-stretch gap-x-4 @3xl:gap-x-0 gap-y-0 @3xl:gap-y-2 @3xl:w-full',
    },
  },
  defaultVariants: {
    orientation: 'auto',
  },
});

const milestoneVariants = cva('flex', {
  variants: {
    orientation: {
      vertical: 'flex-col',
      horizontal: 'flex-row gap-1 justify-center',
      auto: 'flex-col @3xl:flex-row @3xl:gap-1 @3xl:justify-center',
    },
  },
  defaultVariants: {
    orientation: 'auto',
  },
});

const progressBarVariants = cva('relative w-full', {
  variants: {
    orientation: {
      vertical: 'min-w-14 flex flex-col items-center',
      horizontal: 'min-w-32 min-h-14 flex flex-row items-center',
      auto: 'min-w-14 @3xl:min-w-32 @3xl:min-h-14 flex flex-col @3xl:flex-row items-center',
    },
  },
  defaultVariants: {
    orientation: 'auto',
  },
});

const leftPartProgressBarVariants = cva('', {
  variants: {
    orientation: {
      vertical: 'w-2 h-5',
      horizontal: 'w-1/2 h-2',
      auto: 'w-2 @3xl:w-1/2 h-5 @3xl:h-2',
    },
  },
  defaultVariants: {
    orientation: 'auto',
  },
});

const rightPartProgressBarVariants = cva('', {
  variants: {
    orientation: {
      vertical: 'w-2 h-full',
      horizontal: 'w-1/2 h-2',
      auto: 'w-2 @3xl:w-1/2 h-full @3xl:h-2',
    },
  },
  defaultVariants: {
    orientation: 'auto',
  },
});

const milestoneIconVariants = cva(
  'absolute mx-auto left-0 right-0 rounded-full flex justify-center items-center bg-primary border-4 border-card',
  {
    variants: {
      orientation: {
        vertical: 'top-[-2px]',
        horizontal: 'top-auto',
        auto: 'top-[-2px] @3xl:top-auto',
      },
    },
    defaultVariants: {
      orientation: 'auto',
    },
  },
);

const milestoneTextVariants = cva('flex flex-col text-balance leading-tight', {
  variants: {
    orientation: {
      vertical: 'pb-4 text-start text-balance',
      horizontal: 'items-center pb-0 max-w-60 min-w-40 px-2 text-center',
      auto: '@3xl:items-center pb-4 @3xl:pb-0 @3xl:max-w-60 @3xl:min-w-40 @3xl:px-2 text-start @3xl:text-center',
    },
  },
  defaultVariants: {
    orientation: 'auto',
  },
});

/**
 * TrackingTimeline displays a visual timeline of milestones, indicating progress and completion status.
 *
 * @param props.milestones - An array of milestone snapshot objects to display in the timeline.
 * @param props.locale - The locale string used for date and translation formatting.
 * @param props.orientation - The orientation of the timeline ('vertical', 'horizontal', or 'auto').
 * @returns A scrollable timeline component with milestone icons, progress bars, and labels.
 */
export function TrackingTimeline(props: Readonly<TrackingTimelineProps>) {
  const { className, orientation, milestones, locale, ...rest } = props;

  const completedMilestoneCount = milestones.filter((milestone) => milestone.completed).length;

  const scrollViewportRef = React.useRef<HTMLDivElement | null>(null);
  const currentMilestoneRef = React.useRef<HTMLDivElement | null>(null); // Keep track of the latest completed milestone to scroll to it

  // Scroll to the latest completed milestone on load
  useEffect(() => {
    if (scrollViewportRef.current && currentMilestoneRef.current) {
      const offsetLeft = currentMilestoneRef.current.offsetLeft;
      const milestoneWidth = currentMilestoneRef.current.clientWidth;
      const viewportWidth = scrollViewportRef.current.clientWidth;
      const padding = 4 * 6; // Same as px-6

      const toScroll =
        offsetLeft + // Start of the milestone
        milestoneWidth / 2 - // Center the milestone
        viewportWidth / 2 + // Account for the viewport width
        padding; // Account for the padding

      scrollViewportRef.current.scrollTo({
        left: toScroll,
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [completedMilestoneCount]);

  const dateLocale = locale === 'en' ? 'en-GB' : locale;

  return (
    <ScrollArea className="w-full @container" viewportRef={scrollViewportRef}>
      <div className={cn(timelineVariants({ orientation }), className)} {...rest}>
        {milestones.map((milestone, i) => {
          const Icon = getTrackingIcon(milestone.icon);
          const isCurrentMilestone = i === completedMilestoneCount - 1; // If it's the latest completed milestone

          const translation =
            milestone.translations[locale] || // Try to find with the current locale
            milestone.translations.en || // Fallback to English
            Object.values(milestone.translations)[0]; // Fallback to the first translation

          return (
            <React.Fragment key={milestone.id}>
              {/* Above/left of the bar */}
              <div className={cn(milestoneVariants({ orientation }))}>
                {milestone.completed && (
                  <>
                    <span className="text-sm font-semibold text-foreground">
                      {new Date(milestone.timeOfCompletion).toLocaleString(dateLocale, {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>

                    <span className="text-sm text-foreground/80">
                      {new Date(milestone.timeOfCompletion).toLocaleString(dateLocale, {
                        hour: 'numeric',
                        minute: 'numeric',
                      })}
                    </span>
                  </>
                )}
              </div>

              {/* Progress bar */}
              <div
                ref={isCurrentMilestone ? currentMilestoneRef : undefined} // Keep track of the latest completed milestone
                className={cn(
                  progressBarVariants({ orientation }),
                  i === milestones.length - 1 ? 'min-h-10' : 'min-h-16', // The last milestone don't need any bottom padding
                )}
              >
                {/* Left/top part of the progress bar. Goes from the edge to the middle of the icon */}
                <div
                  className={cn(
                    leftPartProgressBarVariants({ orientation }),
                    i < completedMilestoneCount ? 'bg-primary' : 'bg-muted', // If this milestone is completed, show the line
                    i === 0 && 'invisible', // Show no line to the left/top of the first milestone
                  )}
                />

                {/* Right/bottom part of the progress bar. Goes from the middle of the icon to the edge */}
                <div
                  className={cn(
                    rightPartProgressBarVariants({ orientation }),
                    i < completedMilestoneCount - 1 ? 'bg-primary' : 'bg-muted', // If the next milestone is completed, show the line
                    i === milestones.length - 1 && 'invisible', // Show no line to the right/bottom of the last milestone
                  )}
                />

                {/* Milestone icon */}
                <div
                  className={cn(
                    milestoneIconVariants({ orientation }),
                    isCurrentMilestone ? 'w-14 h-14' : 'w-10 h-10', // Put emphasis on the latest completed milestone
                    milestone.completed ? 'bg-primary' : 'bg-muted',
                  )}
                >
                  {Icon && (
                    <Icon
                      weight="bold"
                      className={cn(
                        isCurrentMilestone ? 'w-6 h-6' : 'w-4 h-4',
                        milestone.completed
                          ? 'fill-primary-foreground'
                          : 'fill-muted-foreground/50',
                      )}
                    />
                  )}
                </div>
              </div>

              {/* Below/right of the bar */}
              <div className={cn(milestoneTextVariants({ orientation }))}>
                <span
                  className={cn(
                    'font-medium @3xl:pb-1',
                    !milestone.completed && 'text-card-foreground/60',
                  )}
                >
                  {translation?.name}
                </span>

                <span className="text-sm text-card-foreground/70">
                  {milestone.completed && translation?.description}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
